import { useEffect, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useDispatch } from 'react-redux'
import { useRecentlyProductList } from '@/components/domains/mypage/favorites/hooks/use-recently-product-list'
import { POLICY } from '@/constants/legacy/constData'
import { useProductEventTracker } from '@/containers/event-tracker/product.event-tracker'
import { PRODUCT_QUERY_GQLS } from '@/containers/gqls/products/queries'
import useCartNew from '@/containers/hooks/cart/useCartNew'
import useSkuCodeQuantityValidator from '@/containers/hooks/useSkuCodeQuantityValidator'
import { IModelProduct } from '@/containers/models/modelProduct'
import { IModelProductOption } from '@/containers/models/modelProductOption'
import {
  CartProductOptionKeyType,
  CartProductOptionsAmountType,
  CartProductOptionType,
  CartProductOptionTypeUtil,
} from '@/containers/types/CartProductOption'
import { AdPetType } from '@/graphql/generated/schemas'
import { doSetGlobalSnackBarType } from '@/stores/reduxUI'
import { getResultFromData } from '@/utils/utilApi'
import { deepcopy } from '@/utils/utilData'

type UseProductType = {
  product: IModelProduct | undefined
  cartProductOptions: CartProductOptionType[] // 상품의 옵션들(default 옵션은 미포함)
  cartProductOptionsSelected: CartProductOptionType[] // 선택된 옵션들
  cartProductOptionsSelectedAmount: CartProductOptionsAmountType | undefined // 선택된 옵션들의 금액
  recommendBundledProducts: IModelProduct[]
  isBundleProductsLoading: boolean
  queryProduct: (productId: string, productPromotionId?: string) => void
  queryProductListPageForCart: (productId: string, productPromotionId?: string) => Promise<void>
  queryAddCartItemForOrderHistory: (
    quantity: number,
    productOptionId: string,
    productPromotionId?: string
  ) => Promise<void>
  queryRecommendBundledProductList: (productId: string) => void
  addCartProductOption: (cartProductOption: CartProductOptionType) => void
  updateCartProductOptionPurchaseQuantity: (
    cartProductOptionKey: CartProductOptionKeyType,
    newPurchaseQuantity: number,
    isTryToIncreaseSoldOutQuantity: boolean,
    isTryToIncreaseMaxPurchaseQuantityAtOnce: boolean,
    isPurchaseQuantityAvailableAtOnce: boolean
  ) => void
  updateProductInfo: () => Promise<void>
  updateProductOptionsInfo: () => Promise<void>
  removeCartProductOptionSelected: (cartProductOption: CartProductOptionType) => void
  existsCartProductOptionInSelected: (cartProductOption: CartProductOptionType) => boolean
  isValidPurchaseQuantity: (
    cartProductOption: CartProductOptionType,
    newPurchaseQuantity?: number,
    withCandidate?: boolean
  ) => boolean
  addOrUpdateCartProductOptionsSelectedToCart: (options: CartProductOptionType[], showSnackBar?: boolean) => void
  getAvailableMaxPurchaseQuantity: (
    cartProductOption: CartProductOptionType,
    extendPurchaseQuantity?: number,
    withCandidate?: boolean
  ) => number
  getCartProductOptionInCart: (cartProductOptionKey: CartProductOptionKeyType) => CartProductOptionType | undefined
  isAvailableToAddCart: boolean
  initUseProduct: () => void
  cartProductOptionsInCart: CartProductOptionType[]
  isSkuCodeContains: (cartProductOption: CartProductOptionType, withCandidate?: boolean) => boolean
  renderCartValidateMessage: (
    expectedCartProductOption: CartProductOptionType,
    usageFrom: 'productDetail' | 'cart'
  ) => void
  isCartLoaded: boolean
  cartProductOptionsSelectedSetter: (newCartProductOptions: CartProductOptionType[]) => void
  fitCartProductOption: (option: CartProductOptionType) => CartProductOptionType
  isValidCartProductOption: (cartProductOption: CartProductOptionType) => boolean
}

const useProduct = (): UseProductType => {
  const dispatch = useDispatch()
  const [product, setProduct] = useState<IModelProduct | undefined>()
  const [cartProductOptions, setCartProductOptions] = useState<CartProductOptionType[]>([])
  const [cartProductOptionsSelected, setCartProductOptionsSelected] = useState<CartProductOptionType[]>([])
  const [cartProductOptionsSelectedAmount, setCartProductOptionsSelectedAmount] = useState<
    CartProductOptionsAmountType | undefined
  >()
  const [isAvailableToAddCart, setIsAvailableToAddCart] = useState<boolean>(false)
  const [recommendBundledProducts, setRecommendBundledProducts] = useState<any[]>([])
  const [recommendBundledProductsTotalCount, setRecommendBundledProductsTotalCount] = useState(0)

  const {
    cartProductOptionsInCart,
    refreshCartProductOptions,
    addOrUpdateCartProductOptions,
    renderCartValidateMessage,
    isValidCartProductOption,
    isCartLoaded,
  } = useCartNew()
  const skuCodeQuantityValidator = useSkuCodeQuantityValidator()
  const { addRecentlyProduct, addRecentlyProductByPetType } = useRecentlyProductList({ length: 0 })
  const {
    trackClickOrderListAddToCartButtonEvent,
    // trackClickListStartPurchaseButtonEvent
  } = useProductEventTracker()

  useEffect(() => {
    refreshCartProductOptions()
  }, [])

  useEffect(() => {
    if (cartProductOptionsInCart.length > 0) {
      skuCodeQuantityValidator.setSkuCodeQuantityByCartProductOptions(cartProductOptionsInCart)
      if (cartProductOptionsSelected.length > 0) {
        _setCartProductOptionsSelected(deepcopy(cartProductOptionsSelected) || [])
      }
    }
  }, [cartProductOptionsInCart])

  const [queryProductDetail, { refetch: queryProductDetailRefetch }] = useLazyQuery(PRODUCT_QUERY_GQLS.PRODUCT_DETAIL, {
    fetchPolicy: 'no-cache',
    onCompleted: (productData) => {
      if (!productData) {
        return
      }
      const _product: IModelProduct = getResultFromData(productData)?.product.data || {}
      const _cartProductOptions: CartProductOptionType[] = []

      const _productOptions: IModelProductOption[] = []

      // default 옵션 딱 하나만 있을 경우, cartProductOption 반영
      if (_product.isSingleOption && _product?.productProductOptions?.length === 1) {
        _productOptions.push(..._product?.productProductOptions)
        // default 옵션1, 그냥 옵션1인 단일옵션이거나 다중옵션일 경우, isDefault 제외하고 cartProductOption 반영
      } else {
        _productOptions.push(..._product?.productProductOptions.filter((productOption) => !productOption.isDefault))
      }

      _productOptions.forEach((productOption) => {
        _cartProductOptions.push(CartProductOptionTypeUtil.createCartProductOptionByProduct(_product, productOption))
      })

      addRecentlyProductByPetType(_product.id, _product.petType as AdPetType)
      addRecentlyProduct({
        productId: _product.id,
        productPromotionId: _product.productPromotion.id,
        image: _product.productProductImages[0].thumbnail,
      })
      setProduct(_product)
      setCartProductOptions(_cartProductOptions)
    },
  })

  const [queryProductOptionsByProduct, { refetch: queryProductOptionsByProductRefetch }] = useLazyQuery(
    PRODUCT_QUERY_GQLS.PRODUCT_PRODUCT_OPTIONS,
    {
      fetchPolicy: 'no-cache',
      onCompleted: (productData) => {
        if (!productData) {
          return
        }
        const _product: IModelProduct = getResultFromData(productData)?.product.data || {}
        const _cartProductOptions: CartProductOptionType[] = []

        const _productOptions: IModelProductOption[] = []

        // default 옵션 딱 하나만 있을 경우, cartProductOption 반영
        if (_product.isSingleOption && _product?.productProductOptions?.length === 1) {
          _productOptions.push(..._product?.productProductOptions)
          // default 옵션1, 그냥 옵션1인 단일옵션이거나 다중옵션일 경우, isDefault 제외하고 cartProductOption 반영
        } else {
          _productOptions.push(..._product?.productProductOptions.filter((productOption) => !productOption.isDefault))
        }

        _productOptions.forEach((productOption) => {
          _cartProductOptions.push(CartProductOptionTypeUtil.createCartProductOptionByProduct(_product, productOption))
        })

        setProduct(_product)
        setCartProductOptions(_cartProductOptions)

        // trackClickListStartPurchaseButtonEvent(_product)
      },
    }
  )

  const onCompleteQueryProductOption = (data: any) => {
    if (data) {
      const productOptionsData = getResultFromData(data)?.productOptionsWithPromotion.data
      if (!productOptionsData) {
        return
      }

      const quantity = 1
      const productName = productOptionsData[0].product.name
      const product_id = productOptionsData[0].product._id
      const brand = productOptionsData[0].product.brand
      const supplyType = productOptionsData[0].product.supplyType
      const option = CartProductOptionTypeUtil.createCartProductOptionByProductOption(productOptionsData[0], quantity)

      skuCodeQuantityValidator.setSkuCodeQuantityCandidateByCartProductOptions([option])
      const fitOption = fitCartProductOption(option)
      const validResult = getValidateResult([fitOption])

      if (!validResult) {
        renderInValidOptionMessage(fitOption)
        _clear()
        return
      }
      addOrUpdateCartProductOptionsSelectedToCart([option])
      trackClickOrderListAddToCartButtonEvent({
        optionId: option.productOption_id,
        optionName: option.productOptionName,
        productId: product_id,
        productName: productName,
        brandId: brand.id,
        brandName: brand.name,
        supplyType,
      })
    }
  }

  const [queryProductOption] = useLazyQuery(PRODUCT_QUERY_GQLS.CART_PRODUCT_OPTIONS, {
    fetchPolicy: 'no-cache',
    onCompleted: onCompleteQueryProductOption,
  })

  const queryProduct = (productId: string, productPromotionId?: string) => {
    queryProductDetail({
      variables: {
        id: productId,
        ...(productPromotionId ? { productPromotion: productPromotionId } : {}),
        orderBy: 'series',
      },
    })
  }

  const queryProductListPageForCart = async (productId: string, productPromotionId?: string) => {
    await queryProductOptionsByProduct({
      variables: {
        id: productId,
        ...(productPromotionId ? { productPromotion: productPromotionId } : {}),
      },
    })
  }

  const queryAddCartItemForOrderHistory = async (
    quantity: number,
    productOptionId: string,
    productPromotionId?: string
  ) => {
    const inputItem = {
      productOption: productOptionId,
      productPromotion: productPromotionId,
    }
    if (!productPromotionId) {
      delete inputItem.productPromotion
    }
    const jsonInputItem = JSON.stringify([inputItem])
    const optimizeInputItem = jsonInputItem.replace('\\', '')
    await queryProductOption({
      variables: {
        productOptions: optimizeInputItem,
      },
    })
  }

  const queryRecommendBundledProductList = async (productId: string) => {
    if (!productId) {
      return
    }
    if (
      recommendBundledProductsTotalCount !== 0 &&
      recommendBundledProducts.length === recommendBundledProductsTotalCount
    ) {
      return
    }
    await queryRecommendBundledProducts({
      variables: {
        id: productId,
        isSale: true,
        isShown: true,
        isSoldOut: false,
      },
    })
  }

  const [queryRecommendBundledProducts, { loading: isBundleProductsLoading }] = useLazyQuery(
    PRODUCT_QUERY_GQLS.RECOMMEND_BUNDLED_PRODUCTS,
    {
      fetchPolicy: 'no-cache',
      onCompleted(response) {
        if (!response) {
          return
        }

        const result: any = getResultFromData(response)?.recommendBundledProducts

        const dataKey = 'recommendBundledProducts'
        const { data, totalCount }: any = result || { [dataKey]: { totalCount: 0, data: [] } }

        const _concatList = recommendBundledProducts.concat(data)

        setRecommendBundledProducts(_concatList)
        setRecommendBundledProductsTotalCount(totalCount)
      },
    }
  )

  const initUseProduct = () => {
    setProduct(undefined)
    setCartProductOptions([])
    setCartProductOptionsSelected([])
    setCartProductOptionsSelectedAmount(undefined)
    setIsAvailableToAddCart(false)
  }

  const distinguishAddCart = (cartProductOption: CartProductOptionType) => {
    if (!cartProductOption.isValidStockQuantity || !cartProductOption.isPurchaseQuantityAvailableAtOnce) {
      return false
    }
    return true
  }

  const getIsAvailabelToAddCart = (cartProductOptions: CartProductOptionType[]) => {
    if (cartProductOptions.length < 1) {
      return false
    }
    return cartProductOptions.every(distinguishAddCart)
  }

  const getIsAbleIncreasePurchaseQuantity = (availablePurchaseQuantity: number, purchaseQuantityAll: number) => {
    if (availablePurchaseQuantity < 1) {
      return false
    }
    if (purchaseQuantityAll >= POLICY.MAX_PURCHASE_QUANTITY_AT_ONCE) {
      return false
    }
    return true
  }

  const renderInValidOptionMessage = (cartProductOption: CartProductOptionType) => {
    const validateResult = isValidCartProductOption(cartProductOption)
    if (!validateResult) {
      renderCartValidateMessage(cartProductOption, 'productDetail')
    }
  }

  const fitCartProductOption = (option: CartProductOptionType) => {
    const cartProductOptionKey = CartProductOptionTypeUtil.getCartProductOptionKey(option)
    const cartProductOptionInCart = CartProductOptionTypeUtil.getCartProductOption(
      cartProductOptionsInCart,
      cartProductOptionKey
    )

    const isValidStockQuantity = skuCodeQuantityValidator.isValidPurchaseQuantity(option, option.purchaseQuantity, true)
    const isSkuCodeInCart = skuCodeQuantityValidator.isSkuCodeContains(option)
    const purchaseQuantityInCart = cartProductOptionInCart?.purchaseQuantity || 0
    const purchaseQuantityAll = option.purchaseQuantity + purchaseQuantityInCart
    const isPurchaseQuantityAvailableAtOnce =
      CartProductOptionTypeUtil.isPurchaseQuantityAvailableAtOnce(purchaseQuantityAll)
    const isTryToIncreaseMaxPurchaseQuantityAtOnce = !isPurchaseQuantityAvailableAtOnce
    const availablePurchaseQuantity = skuCodeQuantityValidator.getAvailablePurchaseQuantity(option, undefined, true)
    const isAbleIncreasePurchaseQuantity = getIsAbleIncreasePurchaseQuantity(
      availablePurchaseQuantity,
      purchaseQuantityAll
    )

    const fittedOption: CartProductOptionType = {
      ...option,
      isValidStockQuantity,
      isSkuCodeInCart,
      purchaseQuantityInCart,
      isPurchaseQuantityAvailableAtOnce,
      isTryToIncreaseMaxPurchaseQuantityAtOnce,
      isAbleIncreasePurchaseQuantity,
    }
    return fittedOption
  }

  const getValidateResult = (options: CartProductOptionType[]) => options.every(isValidCartProductOption)

  const _setCartProductOptionsSelected = (options: CartProductOptionType[]) => {
    skuCodeQuantityValidator.setSkuCodeQuantityCandidateByCartProductOptions(options)
    const isSingleOption = cartProductOptions.length === 1
    const newOptions = options.map((option) => fitCartProductOption(option))
    const validateResult = getValidateResult(newOptions)

    if (!validateResult) {
      newOptions.forEach((option) => renderInValidOptionMessage(option))
      skuCodeQuantityValidator.setSkuCodeQuantityCandidateByCartProductOptions(cartProductOptionsSelected)
      if (!isSingleOption) {
        return
      }
    }

    setCartProductOptionsSelected(newOptions)
    setCartProductOptionsSelectedAmount(CartProductOptionTypeUtil.getCartProductOptionsAmount(newOptions))
    setIsAvailableToAddCart(getIsAvailabelToAddCart(newOptions))
  }

  const cartProductOptionsSelectedSetter = (newCartProductOptions: CartProductOptionType[]) => {
    return _setCartProductOptionsSelected(newCartProductOptions)
  }

  const addCartProductOption = (cartProductOption: CartProductOptionType) => {
    const _cartProductOptionsSelected = CartProductOptionTypeUtil.addCartProductOption(
      cartProductOptionsSelected,
      cartProductOption
    )
    _setCartProductOptionsSelected(_cartProductOptionsSelected)
  }

  const _updateShippingCartProductOption = (
    cartProductOptionKey: CartProductOptionKeyType,
    updateFunction: (cartProductOption: CartProductOptionType) => void
  ) => {
    const _cartProductOptionsSelected: CartProductOptionType[] = deepcopy(cartProductOptionsSelected)
    const cartProductOptionForUpdate = CartProductOptionTypeUtil.getCartProductOption(
      _cartProductOptionsSelected,
      cartProductOptionKey
    )
    if (cartProductOptionForUpdate) {
      updateFunction(cartProductOptionForUpdate)
      _setCartProductOptionsSelected(_cartProductOptionsSelected)
    }
  }

  const updateCartProductOptionPurchaseQuantity = (
    cartProductOptionKey: CartProductOptionKeyType,
    newPurchaseQuantity: number,
    isTryToIncreaseSoldOutQuantity: boolean,
    isTryToIncreaseMaxPurchaseQuantityAtOnce: boolean,
    isPurchaseQuantityAvailableAtOnce: boolean
  ) => {
    _updateShippingCartProductOption(cartProductOptionKey, (cartProductOptionForUpdate) => {
      cartProductOptionForUpdate.purchaseQuantity = newPurchaseQuantity
      cartProductOptionForUpdate.isTryToIncreaseSoldOutQuantity = isTryToIncreaseSoldOutQuantity
      cartProductOptionForUpdate.isTryToIncreaseMaxPurchaseQuantityAtOnce = isTryToIncreaseMaxPurchaseQuantityAtOnce
      cartProductOptionForUpdate.isPurchaseQuantityAvailableAtOnce = isPurchaseQuantityAvailableAtOnce
    })
  }

  const removeCartProductOptionSelected = (cartProductOption: CartProductOptionType) => {
    const _cartProductOptionsSelected = CartProductOptionTypeUtil.removeCartProductOption(
      cartProductOptionsSelected,
      CartProductOptionTypeUtil.getCartProductOptionKey(cartProductOption)
    )
    _setCartProductOptionsSelected(_cartProductOptionsSelected)
  }

  const existsCartProductOptionInSelected = (cartProductOption: CartProductOptionType) => {
    return CartProductOptionTypeUtil.existsCartProductOption(
      cartProductOptionsSelected,
      CartProductOptionTypeUtil.getCartProductOptionKey(cartProductOption)
    )
  }

  const _clear = () => {
    setCartProductOptionsSelected([])
    setIsAvailableToAddCart(false)
    skuCodeQuantityValidator.clearSkuCodeQuantityCandidate()
  }

  const addOrUpdateCartProductOptionsSelectedToCart = async (options: CartProductOptionType[], showSnackBar = true) => {
    await addOrUpdateCartProductOptions(options)
    if (showSnackBar) {
      dispatch(doSetGlobalSnackBarType('addCart'))
    }
    _clear()
  }

  const getCartProductOptionInCart = (cartProductOptionKey: CartProductOptionKeyType) => {
    return CartProductOptionTypeUtil.getCartProductOption(cartProductOptionsInCart, cartProductOptionKey)
  }

  const updateProductInfo = async () => {
    // eslint-disable-next-line no-unused-expressions
    queryProductDetailRefetch && (await queryProductDetailRefetch())
    const soldOutCartProductOptionsKeys = cartProductOptions
      .filter((productOption) => productOption.checked === false)
      .map((productOption) => CartProductOptionTypeUtil.getCartProductOptionKey(productOption))

    setCartProductOptionsSelected(
      CartProductOptionTypeUtil.removeCartProductOptions(cartProductOptionsSelected, soldOutCartProductOptionsKeys)
    )
  }

  const updateProductOptionsInfo = async () => {
    // eslint-disable-next-line no-unused-expressions
    queryProductOptionsByProductRefetch && (await queryProductOptionsByProductRefetch())
    const soldOutCartProductOptionsKeys = cartProductOptions
      .filter((productOption) => productOption.checked === false)
      .map((productOption) => CartProductOptionTypeUtil.getCartProductOptionKey(productOption))

    setCartProductOptionsSelected(
      CartProductOptionTypeUtil.removeCartProductOptions(cartProductOptionsSelected, soldOutCartProductOptionsKeys)
    )
  }

  return {
    product,
    cartProductOptions,
    cartProductOptionsSelected,
    cartProductOptionsSelectedAmount,
    recommendBundledProducts,
    isBundleProductsLoading,
    queryProduct,
    queryProductListPageForCart,
    queryAddCartItemForOrderHistory,
    queryRecommendBundledProductList,
    addCartProductOption,
    updateCartProductOptionPurchaseQuantity,
    updateProductInfo,
    updateProductOptionsInfo,
    removeCartProductOptionSelected,
    existsCartProductOptionInSelected,
    isValidPurchaseQuantity: skuCodeQuantityValidator.isValidPurchaseQuantity,
    addOrUpdateCartProductOptionsSelectedToCart,
    getAvailableMaxPurchaseQuantity: skuCodeQuantityValidator.getAvailablePurchaseQuantity,
    getCartProductOptionInCart,
    isAvailableToAddCart,
    initUseProduct,
    cartProductOptionsInCart,
    isSkuCodeContains: skuCodeQuantityValidator.isSkuCodeContains,
    renderCartValidateMessage,
    isCartLoaded,
    cartProductOptionsSelectedSetter,
    fitCartProductOption,
    isValidCartProductOption,
  }
}

export default useProduct
